import styles from './naverMap.module.css'

interface NaverLatLng {
  readonly lat?: number
  readonly lng?: number
}

interface NaverMap {
  destroy(): void
}

interface NaverMarker {}

interface NaverInfoWindow {
  open(map: NaverMap, anchor: NaverMarker): void
  close(): void
  getMap(): NaverMap | null
}

interface NaverMapListener {}

interface NaverMapsApi {
  LatLng: new (lat: number, lng: number) => NaverLatLng
  Map: new (
    container: HTMLElement,
    options: { center: NaverLatLng; zoom: number; scrollWheel: boolean },
  ) => NaverMap
  Marker: new (options: { position: NaverLatLng; map: NaverMap }) => NaverMarker
  InfoWindow: new (options: { content: HTMLElement }) => NaverInfoWindow
  Event: {
    once(target: NaverMap, eventName: 'init', listener: () => void): NaverMapListener
    addListener(target: NaverMarker, eventName: 'click', listener: () => void): NaverMapListener
    removeListener(listener: NaverMapListener): void
  }
}

interface NaverGlobal {
  maps: NaverMapsApi
}

declare global {
  interface Window {
    naver?: NaverGlobal
    navermap_authFailure?: () => void
  }
}

const SDK_SELECTOR = 'script[data-naver-map-sdk]'
const SDK_BASE_URL = 'https://oapi.map.naver.com/openapi/v3/maps.js'
const NAVER_MAP_TIMEOUT_MS = 10_000
const authFailureListeners = new Set<() => void>()
let previousAuthFailure: (() => void) | undefined

function dispatchNaverAuthFailure() {
  const listeners = [...authFailureListeners]
  try {
    previousAuthFailure?.()
  } finally {
    for (const listener of listeners) listener()
  }
}

function subscribeNaverAuthFailure(listener: () => void): () => void {
  if (authFailureListeners.size === 0) {
    previousAuthFailure = window.navermap_authFailure
    window.navermap_authFailure = dispatchNaverAuthFailure
  }
  authFailureListeners.add(listener)

  return () => {
    authFailureListeners.delete(listener)
    if (authFailureListeners.size !== 0) return

    if (window.navermap_authFailure === dispatchNaverAuthFailure) {
      if (previousAuthFailure === undefined) Reflect.deleteProperty(window, 'navermap_authFailure')
      else window.navermap_authFailure = previousAuthFailure
    }
    previousAuthFailure = undefined
  }
}

export interface NaverMapVenue {
  name: string
  lat: number
  lng: number
}

export interface NaverMapMountOptions {
  signal?: AbortSignal
  onFailure?: (error: Error) => void
}

export function loadNaverMapsSdk(clientId: string): Promise<NaverMapsApi> {
  if (window.naver?.maps) return Promise.resolve(window.naver.maps)
  if (clientId.trim() === '') return Promise.reject(new Error('NAVER Maps Client ID is required'))

  const existing = document.querySelector<HTMLScriptElement>(SDK_SELECTOR)
  const script = existing ?? document.createElement('script')

  return new Promise((resolve, reject) => {
    let timeoutId = 0
    const cleanup = () => {
      window.clearTimeout(timeoutId)
      script.removeEventListener('load', handleLoad)
      script.removeEventListener('error', handleError)
    }
    const handleLoad = () => {
      cleanup()
      if (window.naver?.maps) resolve(window.naver.maps)
      else reject(new Error('NAVER Maps SDK loaded without its global API'))
    }
    const handleError = () => {
      cleanup()
      reject(new Error('NAVER Maps SDK failed to load'))
    }

    script.addEventListener('load', handleLoad)
    script.addEventListener('error', handleError)
    timeoutId = window.setTimeout(handleError, NAVER_MAP_TIMEOUT_MS)

    if (existing === null) {
      const url = new URL(SDK_BASE_URL)
      url.searchParams.set('ncpKeyId', clientId.trim())
      script.src = url.toString()
      script.async = true
      script.defer = true
      script.dataset.naverMapSdk = 'true'
      document.head.append(script)
    }
  })
}

export function mountNaverMap(
  container: HTMLElement,
  clientId: string,
  venue: NaverMapVenue,
  options: NaverMapMountOptions = {},
): Promise<() => void> {
  const { signal, onFailure } = options

  return new Promise((resolve, reject) => {
    let state: 'loading' | 'ready' | 'disposed' = 'loading'
    let timeoutId = 0
    let mapsApi: NaverMapsApi | undefined
    let map: NaverMap | undefined
    let infoWindow: NaverInfoWindow | undefined
    let initListener: NaverMapListener | undefined
    let markerClickListener: NaverMapListener | undefined
    let unsubscribeAuthFailure = () => {}

    const removeInitListener = () => {
      if (mapsApi !== undefined && initListener !== undefined) mapsApi.Event.removeListener(initListener)
      initListener = undefined
    }
    const removeMarkerClickListener = () => {
      if (mapsApi !== undefined && markerClickListener !== undefined) {
        mapsApi.Event.removeListener(markerClickListener)
      }
      markerClickListener = undefined
    }
    const dispose = () => {
      if (state === 'disposed') return
      state = 'disposed'
      window.clearTimeout(timeoutId)
      signal?.removeEventListener('abort', handleAbort)
      removeInitListener()
      removeMarkerClickListener()
      unsubscribeAuthFailure()
      try {
        infoWindow?.close()
      } catch {
        // 인증 실패 시 SDK가 정보창을 먼저 정리할 수 있다.
      }
      try {
        map?.destroy()
      } catch {
        // 인증 실패 시 SDK가 먼저 지도를 정리할 수 있다.
      }
    }
    const succeed = () => {
      if (state !== 'loading') return
      state = 'ready'
      window.clearTimeout(timeoutId)
      removeInitListener()
      resolve(dispose)
    }
    const fail = (error: Error) => {
      if (state === 'disposed') return
      const wasReady = state === 'ready'
      dispose()
      if (wasReady) onFailure?.(error)
      else reject(error)
    }
    const handleAbort = () => {
      if (state === 'ready') dispose()
      else fail(new DOMException('NAVER Maps initialization aborted', 'AbortError'))
    }

    unsubscribeAuthFailure = subscribeNaverAuthFailure(() => {
      fail(new Error('NAVER Maps authentication failed'))
    })
    if (signal?.aborted) {
      handleAbort()
      return
    }
    signal?.addEventListener('abort', handleAbort, { once: true })
    timeoutId = window.setTimeout(() => {
      fail(new Error('NAVER Maps initialization timed out'))
    }, NAVER_MAP_TIMEOUT_MS)

    void loadNaverMapsSdk(clientId)
      .then((maps) => {
        if (state !== 'loading') return
        mapsApi = maps
        const center = new maps.LatLng(venue.lat, venue.lng)
        map = new maps.Map(container, { center, zoom: 15, scrollWheel: false })
        const marker = new maps.Marker({ position: center, map })
        const infoWindowContent = document.createElement('span')
        infoWindowContent.className = styles.infoWindowContent!
        infoWindowContent.textContent = venue.name
        infoWindow = new maps.InfoWindow({
          content: infoWindowContent,
        })
        markerClickListener = maps.Event.addListener(marker, 'click', () => {
          if (infoWindow?.getMap()) infoWindow.close()
          else if (map !== undefined) infoWindow?.open(map, marker)
        })
        const listener = maps.Event.once(map, 'init', succeed)
        initListener = listener
        if (state !== 'loading') maps.Event.removeListener(listener)
      })
      .catch((error: unknown) => {
        fail(error instanceof Error ? error : new Error('NAVER Maps initialization failed'))
      })
  })
}
